import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";
import { Besokstyp } from "@/tabs/Oppenvard/types";

type State = BaseState & {
    besokstyper: Map<Besokstyp["id"], Besokstyp>;
};

export const convertBesokstypData: (args: { besokstyp?: Besokstyp }) => { value: number; label: string } | null = ({
    besokstyp,
}) => {
    if (!besokstyp) {
        return null;
    }

    return {
        value: besokstyp.id,
        label: besokstyp.namn,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        besokstyper: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "besokstyper";
const baseUrl = "oppenvard/besokstyper/";

type Getters = {
    besokstypById: (data: { id?: number }) => Besokstyp | null;
};

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        besokstyp: (state: State) => [...state.besokstyper.values()],
        besokstypSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                limit: 0,
                ...props, // Skriver över limit om den finns i props
                state,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertBesokstypData({ besokstyp: singleResult }));
            }

            return result;
        },
        // används av getBesoksTypById action
        besokstypById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.besokstyper.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addBesokstyper: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                besokstyper = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                besokstyper: Besokstyp[];
            }
        ) =>
            subsetSetter<State, Besokstyp>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: besokstyper,
            }),
        updateBesokstyp: (
            state: State,
            {
                besokstyper,
                offset,
                subsetName,
            }: {
                besokstyper: Besokstyp[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Besokstyp>({
                offset,
                state,
                collectionName: moduleName,
                items: besokstyper,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        getBesoksTypById: async (
            { getters, commit }: BaseActionsProps<State> & { getters: Getters },
            { id, displayMode = true }: { id?: number; displayMode?: boolean }
        ) => {
            if (!id) {
                return null;
            }
            const besokFromStore = getters.besokstypById({ id });

            if (besokFromStore) {
                return displayMode ? convertBesokstypData({ besokstyp: besokFromStore }) : besokFromStore;
            }

            try {
                const { data } = await klinikenApi.get<Besokstyp>(`${baseUrl}${id}`, getConfig());

                commit("addBesokstyper", {
                    subsetName: id.toString(),
                    offset: 0,
                    besokstyper: [data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },

        fetchBesokstyper: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
                limit = 20,
                page = 1,
            }: {
                limit: number;
                page: number;
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Besokstyp>({
                    filterParams,
                    state,
                    config,
                    page,
                    limit,
                    collectionName: moduleName,
                    apiUrl: baseUrl,
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addBesokstyper", {
                    subsetName,
                    offset,
                    besokstyper: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertBesokstypData({ besokstyp: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createBesokstyp: async (
            { commit, state }: BaseActionsProps<State>,
            {
                besokstyp,
            }: {
                besokstyp: Besokstyp;
            }
        ) => {
            try {
                const response = await klinikenApi.post(baseUrl, besokstyp, getConfig({}));

                // TODO: Hur får jag den att läggas till i alla subsets som den ska ingå i?
                commit("addBesokstyper", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    besokstyper: [response.data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateBesokstyp: async (
            { commit, state }: BaseActionsProps<State>,
            {
                besokstyp,
            }: {
                besokstyp: Besokstyp;
            }
        ) => {
            try {
                const response = await klinikenApi.put(`${baseUrl}${besokstyp.id}/`, besokstyp, getConfig({}));

                commit("updateBesokstyp", {
                    subsetName: "newlyUpdated",
                    besokstyper: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
